import React, { Component } from 'react';
import { XCircle, RefreshCcw, Edit, Eye, PlusCircle } from 'react-feather';
import PropTypes from 'prop-types';
import { Field, FieldArray, ErrorMessage } from 'formik';
import { isEmpty } from 'lodash';

import './css/basicTopicDetails.css';

import ImageDnD from '../common/form/imageDnD';
import BSFormControl from '../common/form/bsFormControl';
import ServerErrorMessage from '../common/serverErrorMessage';
import AutosuggestTagsSelector from '../common/form/autosuggestTagsSelector';
import { applyImageFit, getBase64Data, getValueByFieldPath, getVoterPreviewUrl } from '../../helpers/utils';
import logger from '../../helpers/logger';
import { CONFIRM_MODAL } from '../../helpers/modalTypes';
import { LINK_IMAGE_GIF_MAX_SIZE, LINK_SOURCES_MAX_COUNT, LINK_IMAGE_DIMENSIONS } from '../../helpers/topicBuilderHelper';
import FeaturesContext from "../../scenes/contexts/featuresContext";
import { ENTITY_FEATURES } from "hollerlive-shared/constants";
import { COMPARATORS } from "../../helpers/featuresHelper";
import messages from '../../helpers/messages';
import NoPermissionLabel from '../common/noPermissionLabel';

export class LinkConfiguration extends Component {
	constructor(props, context) {
		super(props, context);
		const { linkConfig } = props.values;

		this.state = {
			imgPreview : linkConfig.linkImage,
			isPreviewEnabled: true
		};

		this.handleFieldValueChange = this.handleFieldValueChange.bind(this);
		this.switchLinkConfigMode = this.switchLinkConfigMode.bind(this);
		this.handleSourceSelect = this.handleSourceSelect.bind(this);
		this.handleAddSourcesLinkClick = this.handleAddSourcesLinkClick.bind(this);
	}

	async handleImageUpload(imageObj, setFieldValue) {
		try {
			if (imageObj.type === 'image/gif') {
				const processedImage = await getBase64Data(imageObj);
				setFieldValue('linkConfig.linkImage', processedImage);
				this.setState({ imgPreview: processedImage });
			} else {
				const processedImage = await applyImageFit(imageObj, LINK_IMAGE_DIMENSIONS.WIDTH, LINK_IMAGE_DIMENSIONS.HEIGHT);
				setFieldValue('linkConfig.linkImage', processedImage);
				this.setState({ imgPreview: processedImage });
			}
		} catch (err) {
			logger.error(err);
		}
	}

	handleDeleteLinkSourcesClick(sourcesLinkId) {
		const { sources } = this.props.values.linkConfig;
		let configEntry = sources.find(sourceObj => sourceObj.id === sourcesLinkId);
		
		if (isEmpty(configEntry.value)) {
            this.handleConfirmDeleteLink(sourcesLinkId);
        } else {
            this.handleOpenConfirmDelete(configEntry);
        }
	}

	handleOpenConfirmDelete(linkConfig) {
        const modalConfig = {
            title: 'Confirm Delete',
			message: `Are you sure you want to delete configuration for voting link: "${getVoterPreviewUrl(this.props.values.basicSettings.title, linkConfig.value)}"?`,
            onClose: () => this.context.hideModal(),
            onConfirm: () => this.handleConfirmDeleteLink(linkConfig.id),
            isConfirmModal: true
        };

        this.context.showModal(CONFIRM_MODAL, modalConfig);
	}
	
	handleConfirmDeleteLink(sourcesLinkId) {
        this.context.hideModal();
		const { values, setValues } = this.props;
		const { sources } = values.linkConfig;

		const newFormValuesState = Object.assign({}, values, {
			linkConfig: Object.assign({}, values.linkConfig, {
				sources: sources.filter(s => s.id !== sourcesLinkId)
			})
		});

		setValues(newFormValuesState);
	}

	handleAddSourcesLinkClick() {
		const { values, setValues, setFieldTouched } = this.props;
		const { sources } = values.linkConfig;

		let newId = 0;
		if (sources.length > 0) {
			const orederdSources = [...sources].sort((a, b) => Number(b.id) - Number(a.id));
			newId = Number(orederdSources[0].id) + 1;
		}

		const newLinkWithSources = {
			id: newId,
			value: ""
		};

		const newFormState = Object.assign({}, values, {
			linkConfig: Object.assign({}, values.linkConfig, {
				sources: [...sources, newLinkWithSources]
			})
		});

		setValues(newFormState);
		const fieldPath = `linkConfig.sources.${sources.length}.value`;
		setFieldTouched(fieldPath);
	}

	onImageError(setFieldError, reason) {
		setFieldError('linkConfig.linkImage', reason);
	}

	fieldHasErrors(errors = {}, field) {
		const fieldError = getValueByFieldPath(errors, field);
		return fieldError && fieldError.length > 0;
	}

	handleFieldValueChange(field, value) {
		this.props.setFieldValue(field, value);
	}

	switchLinkConfigMode(shouldEnablePreview) {
		this.setState({ isPreviewEnabled: shouldEnablePreview });
	}

	handleOpenConfirmReset() {
		const modalConfig = {
			title: 'Confirm Reset',
			message: 'Are you sure you want to reset to default link image and description?',
			onClose: () => this.context.hideModal(),
			onConfirm: () => this.onConfirmReset(),
			isConfirmModal: true
		};
		this.context.showModal(CONFIRM_MODAL, modalConfig);
	}

	handleSourceSelect(sourcesLinkId, selectedSources, fieldPath) {
		const { setFieldValue, setFieldTouched } = this.props;

		const updatedLinkConfigValue = this.buildLinkConfigValueFromSources(selectedSources);
		
		setFieldValue(fieldPath, updatedLinkConfigValue);
		setFieldTouched(fieldPath, true, true);
	}

	onConfirmReset() {
		this.context.hideModal();
		this.handleFieldValueChange('linkConfig.linkDescription', '');
		this.clearImage();
	}

	clearImage() {
		this.handleFieldValueChange('linkConfig.linkImage', undefined);
		this.setState({ imgPreview: null });
	}

	buildLinkConfigValueFromSources(sourceValues) {
		return sourceValues.map(sourceValue => sourceValue.name.trim()).join(',');
	}

	_renderLinkConfig() {
		const {
			backendErrors,
			errors,
			values,
			setFieldValue,
			setFieldError,
		} = this.props;

		if (this.state.isPreviewEnabled === false) {
			return (
				<div>
					<p className="link-text-description">Upload a custom image and input custom description text</p>
					<BSFormControl
						label={'link image'}
						hasErrors={this.fieldHasErrors(errors, 'linkConfig.linkImage')}
						subLabel={'a custom image used in sharing link with maximum resolution of 1200px by 630px. can be a gif with maximum size of 300kb'}
					>
						<Field name="linkConfig.linkImage">
							{({ field, form }) =>
								<div className="form-group">
									<div className="images-container">
										<div className="image-dnd-container">
												<ImageDnD
													onDrop={(file) => this.handleImageUpload(file, setFieldValue)}
													image={field.value}
													onRejectedFile={(reason) => this.onImageError(setFieldError, reason)}
													onClear={() => this.clearImage()}
													showClearButton={true}
													maxGifSize={LINK_IMAGE_GIF_MAX_SIZE}
													className={'imageWrapper' + (this.fieldHasErrors(errors, 'linkConfig.linkImage') ? ' imageWrapperError' : '')}
												/>
										</div>
										<div className="image-preview image-content-end">
											<div className="imageWrapper">
												<img src={this.state.imgPreview} alt="" />
											</div>
										</div>
									</div>
									<div>
										{form.errors && form.errors.linkConfig && form.errors.linkConfig.linkImage &&
											<span className='control-label'>{form.errors.linkConfig.linkImage}</span>
										}
										<ServerErrorMessage errors={backendErrors} name="linkConfig.linkImage" className="control-label" />
									</div>
								</div>
							}
						</Field>
					</BSFormControl>

					<BSFormControl
						label={'link description text'}
						subLabel={'a maximum of 300 characters descriptive text following your link. use this text to provide description in desired language'}
						hasErrors={this.fieldHasErrors(errors, 'linkConfig.linkDescription')}
					>
						<Field type="text" name="linkConfig.linkDescription" className="form-control" placeholder={values.linkConfig.linkDescription} />
						<ErrorMessage name="linkConfig.linkDescription" component="span" className='control-label' />
					</BSFormControl>

					<div className='input-group-btn text-right'>
						<button type='button' className='btn btn-outline' onClick={() => { this.switchLinkConfigMode(true); }}>
							<Eye size='16' /> Preview link
                </button>
					</div>
				</div>
			);
		}
	}

	getLinkSourceItems() {
		const { errors } = this.props;
		const { linkConfig, basicSettings } = this.props.values;
		const { sources } = linkConfig;

		return sources.map((sourceObj, indexS) => {
			const selectionTagObj = sourceObj.value.split(',').map((sourceValue, indexSValue) => { 
				return { id: indexSValue, name: sourceValue || "" };
			});

			const fieldPath = `linkConfig.sources.${indexS}.value`;

			return <div key={indexS} className="form-group ordered-form-fields uitest-sources-fields">
				<div className="ordered-form-fields-item with-action">
					<div className="number">{indexS + 1}</div>

					<div className="form-control-container">
						<BSFormControl required hasErrors={this.fieldHasErrors(errors, fieldPath)}>
							<AutosuggestTagsSelector
								name={fieldPath}
								value={selectionTagObj}
								data={this.props.sources}
								onChange={(selectedSources) => this.handleSourceSelect(sourceObj.id, selectedSources, fieldPath)}
								className={`form-control uitest-sources-field`}
								maxSelections={LINK_SOURCES_MAX_COUNT}
							/>
							<ErrorMessage name={fieldPath} component="div" className='control-label' />
							<p className="message">voting link preview: <strong>{ getVoterPreviewUrl(basicSettings.title, sourceObj.value) }</strong></p>
						</BSFormControl>
					</div>

					<span className="action">
						<XCircle className="text-danger" onClick={() => this.handleDeleteLinkSourcesClick(sourceObj.id)} />
					</span>
				</div>
			</div>;
		});
	}

	_renderLinkPreview() {
		const { values } = this.props;
		const { linkConfig, basicSettings } = values;
		const isCustomized = linkConfig.linkImage || linkConfig.linkDescription;

		if (this.state.isPreviewEnabled === true) {
			return (
				<div>
					<FeaturesContext 
					requiredFeatures={[{
						name: ENTITY_FEATURES.LINK_SHARING_CONFIG,
						compare: COMPARATORS.IS_TRUE
					}]}
					renderOtherwise={<p className="link-text-description">
						Social media sharing link will be styled like shown below.
					</p>}
					>
						<p className="link-text-description">
							Social media sharing link will be styled like shown below. You can&nbsp;
							<button className="btn-link" onClick={() => { this.switchLinkConfigMode(false); }}>change</button>
							&nbsp;the social media description text and image.
						</p>
					</FeaturesContext>
					
					<div className='link-preview-wrapper'>
						<div className='link-preview'>
							<div ref={linkPreviewCaption => this.linkPreviewCaption = linkPreviewCaption}>
								<p><span className="text-primary">holler.live</span> - {basicSettings.displayName ? basicSettings.displayName : basicSettings.title} </p>
								<p>{linkConfig.linkDescription || `I voted on ${basicSettings.displayName ? basicSettings.displayName : basicSettings.title}, click to vote and see who agrees with you.`}</p>
							</div>
							<div className="link-preview-img">
								<img className="img-responsive" src={linkConfig.linkImage || "https://holler.live/images/ogimage.jpg"} alt=""
									onLoad={() => this.linkPreviewCaption.className = 'link-preview-caption'} />
							</div>
						</div>
					</div>

					<div className='input-group-btn text-right right-childs-margin-half-vw'>
						{isCustomized &&
							<button type='button' className='btn btn-outline' onClick={() => this.handleOpenConfirmReset()}>
								<RefreshCcw size='16' /> Reset to default
							</button>
						}
						
						<FeaturesContext 
							requiredFeatures={[{
								name: ENTITY_FEATURES.LINK_SHARING_CONFIG,
								compare: COMPARATORS.IS_TRUE
							}]}
							renderOtherwise={
								<button type='button' disabled={true} className='btn btn-outline btn-primary' onClick={() => {}}>
	                                <NoPermissionLabel message={messages.permissions.features.noLinkShare} />
									<Edit size='16'/> Customize
								</button>
							}
						>
							
							<button type='button' className='btn btn-outline btn-primary'
									onClick={() => this.switchLinkConfigMode(false)}>
								<Edit size='16'/> Customize
							</button>
						</FeaturesContext>
					
					</div>
				</div>
			);
		}
	}

	render() {
		return (
				<div className="link-configuration">
					<div className="link-config-preview">
						{this._renderLinkConfig()}
						{this._renderLinkPreview()}
					</div>
					
					<div className="links-with-sources">
						<h4>
							Voting link sources
							<span className="subtitle">
								Add voting links with sources for votes tracking. Each voting link can include up to 3 source names. 
								Each source name can contain up to 20 alpha-numeric characters.
							</span>
						</h4>
						
						<div className="sources-fields">
							<FieldArray
								name="linkConfig.sources"
								render={() => (
									<div className="row-fluid clearfix">
										{this.getLinkSourceItems()}
										
										<FeaturesContext 
											requiredFeatures={[{
												name: ENTITY_FEATURES.LINK_SOURCES_CONFIG,
												compare: COMPARATORS.IS_TRUE
											}]}
											renderOtherwise={
												<button
													className="btn btn-dotted btn-outline btn-success btn-full-width"
													onClick={() => {}}
													type="button"
													disabled={true} 
												>
                                    				<NoPermissionLabel message={messages.permissions.features.noCustomSources} />
													<PlusCircle size={16}/> add a link with sources
												</button>
											}
										>
											
											<button
												className="btn btn-dotted btn-outline btn-success btn-full-width"
												onClick={this.handleAddSourcesLinkClick}
												type="button"
											>
												<PlusCircle size={16}/> add a link with sources
											</button>
										
										</FeaturesContext>
									
									</div>
								)}
							/>
						</div>
					</div>
				</div>
		);
	}
}

LinkConfiguration.contextTypes = {
	showModal: PropTypes.func,
	hideModal: PropTypes.func
};

LinkConfiguration.propTypes = {
	values: PropTypes.shape({
		basicSettings: PropTypes.shape({
			title: PropTypes.string.isRequired,
			displayName: PropTypes.string.isRequired
		}).isRequired,
		linkConfig: PropTypes.shape({
			linkDescription: PropTypes.string,
			linkImage: PropTypes.string,
			sources: PropTypes.arrayOf(PropTypes.shape({
				id: PropTypes.number.isRequired,
				value: PropTypes.string.isRequired
			}))
		}).isRequired
	}).isRequired,
	sources: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.number,
		value: PropTypes.string
	})),
	backendErrors: PropTypes.shape({
		linkConfig: PropTypes.object
	}),
	errors: PropTypes.object,
	editMode: PropTypes.bool,
	setValues: PropTypes.func.isRequired,
	setFieldValue: PropTypes.func.isRequired,
	setFieldTouched: PropTypes.func,
	setFieldError: PropTypes.func.isRequired,
	isDirty: PropTypes.bool
};

export default LinkConfiguration;
